import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { calculateJoursOuvres } from '@/lib/heures-utils';

interface AffectationParams {
  membreId: string;
  chantierId: string;
  dateDebut: string;
  dateFin: string;
  heuresParJour?: number; // Heures par jour (ancien format)
  heuresJour1?: number; // Heures pour le premier jour (nouveau format rotation)
  heuresJoursSuivants?: number; // Heures pour les jours suivants (nouveau format rotation)
}

/**
 * Hook pour gérer l'affectation d'un membre à un chantier
 * Crée l'affectation avec les paramètres fournis
 */
export function useAffectationChantier() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const affecterAuChantier = async ({
    membreId,
    chantierId,
    dateDebut,
    dateFin,
    heuresParJour,
    heuresJour1,
    heuresJoursSuivants,
  }: AffectationParams) => {
    setIsLoading(true);
    setError(null);

    try {
      // 1. Récupérer le membre pour taux_horaire et charges
      const { data: membre, error: membreError } = await supabase
        .from('membres_equipe')
        .select('taux_horaire, charges_patronales')
        .eq('id', membreId)
        .single();

      if (membreError) throw membreError;
      if (!membre) throw new Error('Membre non trouvé');

      // 2. Calculer les jours ouvrés
      const joursOuvres = calculateJoursOuvres(dateDebut, dateFin);

      // 3. Calculer heures_par_jour
      // Si nouveau format rotation (heuresJour1 + heuresJoursSuivants), calculer moyenne pondérée
      // Sinon utiliser heuresParJour (ancien format)
      let heuresParJourFinal: number | undefined = heuresParJour;

      if (heuresJour1 !== undefined && heuresJoursSuivants !== undefined && joursOuvres > 0) {
        if (joursOuvres === 1) {
          // Un seul jour = heures du jour 1
          heuresParJourFinal = heuresJour1;
        } else {
          // Plusieurs jours = moyenne pondérée (jour 1 + jours suivants)
          const totalHeures = heuresJour1 + (heuresJoursSuivants * (joursOuvres - 1));
          heuresParJourFinal = totalHeures / joursOuvres;
        }
      }

      // 4. Créer l'affectation
      const { error: insertError } = await supabase
        .from('affectations_chantiers')
        .insert({
          membre_equipe_id: membreId,
          chantier_id: chantierId,
          date_debut: dateDebut,
          date_fin: dateFin,
          jours_travailles: joursOuvres,
          heures_par_jour: heuresParJourFinal,
          taux_horaire_specifique: membre.taux_horaire,
          charges_patronales_pct: membre.charges_patronales,
        });

      if (insertError) throw insertError;
    } catch (err) {
      const e = err instanceof Error ? err : new Error("Erreur lors de l'affectation");
      setError(e);
      throw e;
    } finally {
      setIsLoading(false);
    }
  };

  return { affecterAuChantier, isLoading, error };
}
